/**
 * Rider Earnings dashboard (Phase 4 #12).
 *
 * KPI tiles: today / week / lifetime earnings + balance owed by tenant.
 * COD tile: shows cash currently held by the rider (from COD deliveries
 * not yet remitted) + a Remit-cash CTA.
 * Ledger: paginated + filterable across all kinds (EARNING, PAYOUT,
 * ADJUSTMENT, COD_COLLECTED, COD_REMITTED, COD_REMIT_PENDING).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Wallet, Calendar, TrendingUp, ChevronLeft, RefreshCcw, Coins, BadgeIndianRupee, HandCoins, Banknote, X, Camera, ImageOff } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";

const KIND_LABEL = {
    EARNING: { label: "Delivery", cls: "bg-emerald-100 text-emerald-700" },
    PAYOUT: { label: "Payout", cls: "bg-sky-100 text-sky-700" },
    ADJUSTMENT: { label: "Adjustment", cls: "bg-amber-100 text-amber-700" },
    COD_COLLECTED: { label: "COD collected", cls: "bg-orange-100 text-orange-700" },
    COD_REMITTED: { label: "Cash deposited", cls: "bg-teal-100 text-teal-700" },
    COD_REMIT_PENDING: { label: "Deposit pending", cls: "bg-violet-100 text-violet-700" },
};

const FILTERS = [
    { key: "", testid: "all", label: "All" },
    { key: "EARNING", testid: "earning", label: "Earnings" },
    { key: "PAYOUT", testid: "payout", label: "Payouts" },
    { key: "COD_COLLECTED", testid: "cod-in", label: "COD in" },
    { key: "COD_REMITTED", testid: "cod-out", label: "Cash out" },
    { key: "ADJUSTMENT", testid: "adj", label: "Adjust" },
];

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

export default function RiderEarnings() {
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [remitOpen, setRemitOpen] = useState(false);

    const fetchData = async (currentFilter) => {
        const [s, l] = await Promise.allSettled([
            api.get("/rider/earnings/summary"),
            api.get(`/rider/earnings/ledger${currentFilter ? `?kind=${currentFilter}` : ""}`),
        ]);
        return {
            summary: s.status === "fulfilled" ? s.value.data : null,
            rows: l.status === "fulfilled" ? (l.value.data || []) : null,
        };
    };

    const reload = async () => {
        setLoading(true);
        try {
            const data = await fetchData(filter);
            if (data.summary) setSummary(data.summary);
            if (data.rows) setRows(data.rows);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load earnings");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await fetchData(filter);
                if (cancelled) return;
                if (data.summary) setSummary(data.summary);
                if (data.rows) setRows(data.rows);
            } catch {
                if (!cancelled) toast.error("Failed to load earnings");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [filter]);

    return (
        <div className="bizil-rider max-w-md mx-auto pb-24" data-testid="rider-earnings-page">
            <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white px-4 py-5 rounded-b-3xl">
                <div className="flex items-center gap-2">
                    <Link to="/rider" className="p-1.5 rounded-lg hover:bg-white/10" data-testid="rider-earnings-back"><ChevronLeft size={18} /></Link>
                    <div className="flex-1">
                        <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">My Earnings</p>
                        <p className="font-bold text-lg leading-tight">Balance owed</p>
                    </div>
                    <button onClick={reload} className="p-2 rounded-lg hover:bg-white/10" data-testid="rider-earnings-refresh"><RefreshCcw size={16} /></button>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-4xl font-black" data-testid="rider-balance">{inr(summary?.balance || 0)}</span>
                    <span className="text-xs opacity-80">pending payout</span>
                </div>
                <p className="mt-1 text-[11px] opacity-90 inline-flex items-center gap-1" data-testid="rider-earning-source">
                    <BadgeIndianRupee size={12} />
                    {summary?.earning_source === "override"
                        ? <>Flat <strong>{inr(summary?.override_payout || 0)}</strong>/drop (admin override)</>
                        : <>Earn the order&apos;s <strong>delivery fee</strong> per drop · zone-based</>}
                </p>
            </div>

            <div className="px-4 mt-4 grid grid-cols-3 gap-2">
                <KpiTile icon={Calendar} label="Today" value={summary?.today} testid="rider-kpi-today" tone="emerald" />
                <KpiTile icon={TrendingUp} label="This week" value={summary?.week} testid="rider-kpi-week" tone="sky" />
                <KpiTile icon={Coins} label="Lifetime" value={summary?.total_earned} testid="rider-kpi-lifetime" tone="violet" />
            </div>

            {/* COD tile */}
            <div className="px-4 mt-3">
                <div data-testid="rider-cod-tile" className="bg-orange-50 border border-orange-200 rounded-2xl px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-orange-700 inline-flex items-center gap-1"><HandCoins size={11} /> Cash you&apos;re holding</p>
                            <p className="font-black text-2xl text-orange-800 mt-0.5" data-testid="rider-cash-held">{inr(summary?.cash_held || 0)}</p>
                            <p className="text-[10px] text-orange-700/80 mt-0.5">
                                Collected <strong>{inr(summary?.total_cod_collected || 0)}</strong> · Deposited <strong>{inr(summary?.total_cod_remitted || 0)}</strong>
                            </p>
                        </div>
                        <button
                            data-testid="rider-remit-cta"
                            onClick={() => setRemitOpen(true)}
                            disabled={!summary?.cash_held || summary.cash_held <= 0}
                            className="bg-orange-600 text-white rounded-xl px-3 py-2 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-40"
                        >
                            <Banknote size={14} /> Remit cash
                        </button>
                    </div>
                </div>
            </div>

            <div className="px-4 mt-3">
                <div className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center justify-between text-sm">
                    <div>
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Settled by tenant</p>
                        <p className="font-bold text-emerald-700" data-testid="rider-total-paid">{inr(summary?.total_paid || 0)}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Deliveries</p>
                        <p className="font-bold">{summary?.deliveries || 0}</p>
                    </div>
                </div>
            </div>

            <div className="px-4 mt-5 flex items-center justify-between">
                <h2 className="font-bold text-base">Ledger</h2>
            </div>
            <div className="px-4 mt-2 flex flex-wrap gap-1 text-[11px] font-bold">
                {FILTERS.map(({ key, testid, label }) => (
                    <button
                        key={testid}
                        data-testid={`rider-ledger-filter-${testid}`}
                        onClick={() => setFilter(key)}
                        className={`px-2.5 py-1 rounded-full border ${filter === key ? "bg-foreground text-background border-foreground" : "bg-card border-border text-muted-foreground"}`}
                    >{label}</button>
                ))}
            </div>

            <div className="px-4 mt-3 space-y-2">
                {loading && <p className="text-center text-sm text-muted-foreground py-8"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</p>}
                {!loading && rows.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-10">
                        <Wallet size={28} className="mx-auto mb-2 opacity-40" />
                        No ledger entries yet.
                    </div>
                )}
                {rows.map((r) => {
                    const meta = KIND_LABEL[r.kind] || { label: r.kind, cls: "bg-muted text-muted-foreground" };
                    const negative = (r.kind === "PAYOUT") || (r.kind === "ADJUSTMENT" && r.amount < 0) || r.kind === "COD_REMITTED" || r.kind === "COD_REMIT_PENDING";
                    const isRejected = r.kind === "COD_REMIT_PENDING" && r.rejected_at;
                    const isConfirmed = r.kind === "COD_REMIT_PENDING" && r.confirmed_at;
                    return (
                        <div key={r.id} data-testid={`rider-ledger-row-${r.id}`} className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                                    {r.order_unique_id && <span className="text-[11px] font-bold text-muted-foreground">#{r.order_unique_id}</span>}
                                    {r.payment_method && <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">via {r.payment_method}</span>}
                                    {r.kind === "COD_REMIT_PENDING" && !isConfirmed && !isRejected && (
                                        <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-700">awaiting admin</span>
                                    )}
                                    {isConfirmed && (
                                        <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-emerald-100 text-emerald-700">approved</span>
                                    )}
                                    {isRejected && (
                                        <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-rose-100 text-rose-700">rejected</span>
                                    )}
                                    {r.photo_url && (
                                        <a href={r.photo_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-slate-100 text-slate-700 inline-flex items-center gap-0.5" data-testid={`rider-ledger-row-photo-${r.id}`}>
                                            <Camera size={9} /> photo
                                        </a>
                                    )}
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-1">{fmtDate(r.created_at)}</p>
                                {r.note && <p className="text-xs mt-0.5 truncate">{r.note}</p>}
                                {isRejected && r.rejected_reason && (
                                    <p className="text-[11px] mt-1 text-rose-700" data-testid={`rider-ledger-row-rejected-reason-${r.id}`}>
                                        Rejected: {r.rejected_reason}
                                    </p>
                                )}
                            </div>
                            <p className={`text-sm font-black ${negative ? "text-rose-600" : "text-emerald-700"}`}>
                                {negative ? "−" : "+"}{inr(Math.abs(r.amount))}
                            </p>
                        </div>
                    );
                })}
            </div>

            {remitOpen && (
                <RemitCashModal
                    suggested={summary?.cash_held || 0}
                    onClose={() => setRemitOpen(false)}
                    onSuccess={() => { setRemitOpen(false); reload(); }}
                />
            )}
        </div>
    );
}

function KpiTile({ icon: Icon, label, value, testid, tone }) {
    const tones = {
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
        sky: "bg-sky-50 text-sky-700 border-sky-100",
        violet: "bg-violet-50 text-violet-700 border-violet-100",
    };
    return (
        <div data-testid={testid} className={`border rounded-2xl p-3 ${tones[tone] || tones.emerald}`}>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold opacity-80"><Icon size={11} /> {label}</div>
            <p className="font-black text-lg mt-1">{inr(value || 0)}</p>
        </div>
    );
}

function RemitCashModal({ suggested, onClose, onSuccess }) {
    const [amount, setAmount] = useState(String(Math.max(0, Math.round(suggested))));
    const [note, setNote] = useState("");
    const [photoUrl, setPhotoUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);

    const onPickPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/^image\//.test(f.type)) {
            toast.error("Choose an image file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPhotoUrl(data?.url || "");
            toast.success("Photo attached");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            // reset input so re-picking the same file fires onChange
            e.target.value = "";
        }
    };

    const submit = async () => {
        const v = Number(amount);
        if (!v || v <= 0) return toast.error("Enter a valid amount");
        setBusy(true);
        try {
            await api.post("/rider/earnings/cod-remit", { amount: v, note, photo_url: photoUrl || undefined });
            toast.success("Deposit submitted — awaiting tenant approval");
            onSuccess();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to submit deposit");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0" data-testid="rider-remit-modal">
            <div className="w-full max-w-sm bg-card border border-border rounded-3xl p-5 shadow-2xl">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-lg">Deposit cash to tenant</h3>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted" data-testid="rider-remit-close"><X size={16} /></button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                    Declare the cash amount you&apos;re depositing now. Attach a photo of the cash / receipt — the tenant admin must approve before your <strong>Cash Held</strong> reduces.
                </p>
                <label className="block">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Amount (₹)</span>
                    <input
                        data-testid="rider-remit-amount"
                        type="number"
                        className="inp text-2xl font-black"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                    />
                </label>
                <label className="block mt-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Note (optional)</span>
                    <input data-testid="rider-remit-note" className="inp" placeholder="e.g. Dropped at HQ" value={note} onChange={(e) => setNote(e.target.value)} />
                </label>

                <div className="mt-3">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Photo evidence (optional)</span>
                    {photoUrl ? (
                        <div className="mt-1 relative">
                            <img
                                src={photoUrl}
                                alt="deposit evidence"
                                className="w-full h-36 object-cover rounded-xl border border-border"
                                data-testid="rider-remit-photo-preview"
                            />
                            <button
                                type="button"
                                onClick={() => setPhotoUrl("")}
                                data-testid="rider-remit-photo-clear"
                                className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1.5"
                                aria-label="Remove photo"
                            >
                                <ImageOff size={14} />
                            </button>
                        </div>
                    ) : (
                        <label
                            className={`mt-1 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl py-5 text-sm font-semibold text-muted-foreground cursor-pointer hover:bg-muted/40 ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                            data-testid="rider-remit-photo-pick"
                        >
                            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                            {uploading ? "Uploading…" : "Tap to add photo"}
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickPhoto} data-testid="rider-remit-photo-input" />
                        </label>
                    )}
                </div>

                <button
                    data-testid="rider-remit-submit"
                    onClick={submit}
                    disabled={busy || uploading || !Number(amount)}
                    className="mt-4 w-full bg-orange-600 text-white rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    {busy && <Loader2 size={16} className="animate-spin" />} Submit deposit
                </button>
            </div>
        </div>
    );
}
